/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import React from 'react';
import { Box, Text } from '@chakra-ui/core';
import { useState } from 'react';
import Joi from 'joi';
import { useTranslation } from 'react-i18next';
import { useCurrentUserProfile } from '../../../../../../app/hooks/useCurrentUserProfile';
import { ImagePreviewModal } from '../../../../../../app/components/ImagePreviewModal';
import ImageView from '../../../../../../app/components/ImageView';
import ImageGalleryModal from '../../../../imageGallery/ImageGalleryModal';
import { GalleryImageType } from '../../../../imageGallery/ImageGalleryType';
import { QuestionnaireTemplateStore } from '../../../store/questionnaireTemplateModel';
import { imageSchema } from '../../errorHandlers/formValidation.schema';
import { QUESTIONNAIRE_ELEMENT_TYPE } from '../../../questionnaireTemplate.enums';

interface SelectMCQImageProps {
  elementId: string;
  images: any[];
  onSelect: (images: any[]) => void;
  questionType: QUESTIONNAIRE_ELEMENT_TYPE;
}
export const QuestionSelectImage = React.memo(function QuestionSelectImage({
  elementId,
  onSelect,
  images,
  questionType,
}: SelectMCQImageProps) {
  const { t } = useTranslation('questionnaireTemplate');
  const [open, setOpen] = useState(false);
  const { currentUserProfile } = useCurrentUserProfile();
  const accountId = currentUserProfile?.account ?? '';
  const errorKey = `${elementId}.images`;

  const imageError = QuestionnaireTemplateStore.useStoreState((state) => state.errors[errorKey] ?? null);
  const setFieldError = QuestionnaireTemplateStore.useStoreActions((a) => a.setFieldError);
  const clearFieldError = QuestionnaireTemplateStore.useStoreActions((a) => a.clearFieldError);

  const validateImages = React.useCallback(
    (imgs: any[]) => {
      const { error } = Joi.object({
        images: imageSchema,
        questionType: Joi.string(),
      }).validate({ images: imgs, questionType });

      if (error) {
        const detail = error.details[0];
        setFieldError({ key: errorKey, error: { message: detail.message, context: detail.context } });
      } else {
        clearFieldError(errorKey);
      }
    },
    [questionType, errorKey, setFieldError, clearFieldError],
  );
  function convertToMultipleChoiceImage(image: GalleryImageType): any {
    return {
      galleryId: image.id,
      url: image.url,
      path: image.path,
    };
  }
  const handleSelect = React.useCallback(
    (image: GalleryImageType | null) => {
      const next = image ? [convertToMultipleChoiceImage(image)] : [];
      onSelect(next);
      validateImages(next);
    },
    [onSelect, validateImages],
  );

  const handleRemove = React.useCallback(() => {
    onSelect([]);
    validateImages([]);
  }, [onSelect, validateImages]);

  function convertToGalleryImage(image: any): any {
    return {
      id: image?.galleryId,
      url: image.url,
      account: accountId,
      path: image?.path || '',
    };
  }
  return (
    <Box zIndex={5} position="relative" data-error-key={errorKey} data-testid="question-select-image" aria-invalid={!!imageError}>
      <ImageGalleryModal
        selected={images?.[0] && convertToGalleryImage(images?.[0])}
        onSelected={handleSelect}
        triggerAction={
          <ImageView
            src={images?.[0]?.url}
            showAddImageIcon
            onExpand={() => {
              setOpen(true);
            }}
            onRemove={handleRemove}
            shape="rounded"
            height="42px"
            imagePreviewStyles={imageError ? { border: '1px solid red', backgroundColor: 'rgba(255, 0, 0, 0.1)' } : {}}
          />
        }
      />
      {imageError && (
        <Text
          fontSize="xs"
          color="red.500"
          fontWeight="500"
          mt={2}
          position="absolute"
          top="100%"
          right={0}
          maxWidth="500px"
          width="max-content"
        >
          {t(imageError.message, imageError?.context)}
        </Text>
      )}
      <ImagePreviewModal
        setTrigger={open}
        images={images}
        getImageKey={/* istanbul ignore next */ (image) => image?.galleryId || ''}
        getImageSrc={/* istanbul ignore next */ (image) => image?.url || ''}
        onModalClose={() => setOpen(false)}
      />
    </Box>
  );
});
